import { format } from 'date-fns'
import { CheckCircle, Pencil } from 'lucide-react'

import { Button, useBreakpoint } from 'slab'

import { DeactivatePayScheduleButton } from './DeactivatePayScheduleButton'
import { PayDatePreview } from './PayDatePreview'
import { BankClosureStrategyLabels, PayFrequencyLabels, type PaySchedule } from '../../types/payroll'
import { useGetSamplePayPeriods } from '../hooks/useGetSamplePayPeriods'

interface PayScheduleDetailsProps {
  paySchedule: PaySchedule
  onEditClick: () => void
}

export function PayScheduleDetails ({ paySchedule, onEditClick }: PayScheduleDetailsProps) {
  const { data, isLoading, error } = useGetSamplePayPeriods({
    frequency: paySchedule.frequency,
    bank_closure_strategy: paySchedule.bank_closure_strategy,
    first_day: paySchedule.first_day,
    second_day: paySchedule.second_day,
    reference_pay_date: paySchedule.reference_pay_date,
    start_date: new Date().toISOString().split('T')[0],
    isValid: true,
  })

  const { breakpointIsAtLeast } = useBreakpoint()
  const editButtonText = breakpointIsAtLeast('xs') ? 'Edit schedule' : 'Edit'
  const verifyButtonText = breakpointIsAtLeast('xs') ? 'View and verify schedule' : 'View and verify'

  return (
    <div>
      <div className='grid grid-cols-2 lg:grid-cols-3 gap-8 p-6 border-b border-gray-200'>
        <h2 className='text-xl font-medium col-span-3'>Schedule Details</h2>
        <div className='lg:hidden col-span-3'>
          <PayDatePreview isLoading={isLoading} data={data} error={error?.message} />
        </div>
        <dl className='grid grid-cols-2 col-span-2 gap-4 [&>dt]:label-default [&>dt]:font-medium [&>dt]:text-ds-text-secondary [&>dt]:text-sm [&>dd]:text-sm [&>dd]:font-medium'>
          <dt>Pay frequency</dt>
          <dd>{PayFrequencyLabels[paySchedule.frequency]}</dd>

          <dt>Pay schedule name</dt>
          <dd>{paySchedule.name}</dd>

          {paySchedule.frequency === 'monthly' && (
            <>
              <dt>Monthly pay day</dt>
              <dd>{paySchedule.first_day}</dd>
            </>
          )}
          {paySchedule.frequency === 'semi_monthly' && (
            <>
              <dt>First pay day</dt>
              <dd>{paySchedule.first_day}</dd>
              <dt>Second pay day</dt>
              <dd>{paySchedule.second_day}</dd>
            </>
          )}

          {(paySchedule.frequency === 'bi_weekly' || paySchedule.frequency === 'weekly') && (
            <>
              <dt>Reference pay date</dt>
              <dd>{paySchedule.reference_pay_date && format(new Date(paySchedule.reference_pay_date), 'MMMM d, yyyy')}</dd>
            </>
          )}

          <dt>Bank closure strategy</dt>
          <dd>{BankClosureStrategyLabels[paySchedule.bank_closure_strategy]}</dd>

        </dl>
        <div className='mt-10 hidden lg:block'>
          <PayDatePreview isLoading={isLoading} data={data} error={error?.message} />
        </div>
      </div>

      {paySchedule.source !== 'default' && (
        <div className='flex flex-wrap justify-end items-center p-4'>
          <DeactivatePayScheduleButton
            paySchedule={paySchedule}
          />
          <Button
            variant='tertiary'
            leftIcon={paySchedule.is_verified ? Pencil : CheckCircle}
            onClick={onEditClick}
            data-testid='pay-schedule-details-edit-button'
          >
            {paySchedule.is_verified ? editButtonText : verifyButtonText}
          </Button>
        </div>
      )}
    </div>
  )
}
